/**
 * Fleet Export Helper Functions
 *
 * Exports fleet data including usage stats, shift breakdowns, breaks, capacity analysis,
 * and round-trip Excel/CSV export (importable via Fleet Menu).
 */

import * as XLSX from 'xlsx';
import type { Fleet, Vehicle } from '../components/map/components/FleetMenu';
import type { OperatingPeriod, BreakPeriod } from '../types/simulation';

/** Points with id and coordinates for resolving vehicle start/end locations on export */
export type PointsWithCoords = { id: string; name: string; coordinates?: [number, number] }[];

/**
 * Convert time string to minutes since midnight
 */
function timeToMinutes(timeStr: string): number {
  // Handle both HH:mm and HH:mm:ss formats
  const parts = timeStr.split(':');
  const hours = parseInt(parts[0] || '0', 10);
  const minutes = parseInt(parts[1] || '0', 10);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to time string
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Calculate duration between two time strings in minutes
 */
function calculateDuration(startTime: string, endTime: string): number {
  const start = timeToMinutes(startTime);
  let end = timeToMinutes(endTime);
  // Handle wrap-around (e.g., 23:00 to 03:00)
  if (end < start) {
    end += 24 * 60; // Add 24 hours
  }
  return end - start;
}

/**
 * Generate 15-minute interval time slots for the day
 */
function generateTimeSlots(startHour: number = 3, endHour: number = 23): string[] {
  const slots: string[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      slots.push(minutesToTime(hour * 60 + minute));
    }
  }
  return slots;
}

/**
 * Check if a time point falls within a period (including breaks)
 */
function isTimeInPeriod(
  timeStr: string,
  period: { start: string; end: string; breaks?: BreakPeriod[] },
  excludeBreaks: boolean = false
): boolean {
  const timeMinutes = timeToMinutes(timeStr);
  const startMinutes = timeToMinutes(period.start);
  let endMinutes = timeToMinutes(period.end);
  
  // Handle wrap-around
  if (endMinutes < startMinutes) {
    endMinutes += 24 * 60;
  }
  
  let adjustedTime = timeMinutes;
  if (timeMinutes < startMinutes) {
    adjustedTime += 24 * 60;
  }
  
  if (adjustedTime < startMinutes || adjustedTime >= endMinutes) {
    return false;
  }
  
  // If excluding breaks, check if time falls within any break
  if (excludeBreaks && period.breaks) {
    for (const breakPeriod of period.breaks) {
      if (breakPeriod.start && breakPeriod.end) {
        const breakStart = timeToMinutes(breakPeriod.start);
        let breakEnd = timeToMinutes(breakPeriod.end);
        
        if (breakEnd < breakStart) {
          breakEnd += 24 * 60;
        }
        
        let adjustedBreakTime = adjustedTime;
        if (adjustedBreakTime < breakStart && breakStart >= startMinutes) {
          adjustedBreakTime += 24 * 60;
        }
        
        if (adjustedBreakTime >= breakStart && adjustedBreakTime < breakEnd) {
          return false; // Time is during a break
        }
      }
    }
  }
  
  return true;
}

/**
 * Export fleet analytics to Excel
 */
export function exportFleetAnalytics(fleet: Fleet): void {
  const workbook = XLSX.utils.book_new();
  
  // Sheet 1: Shift Breakdowns and Breaks
  const shiftData: any[][] = [
    [
      'Vehicle',
      'Shift ID',
      'Start Time',
      'End Time',
      'Duration (min)',
      'Break',
      'Break 1 Start',
      'Break 1 End',
      'Break 1 Duration (min)',
      'Break 2 Start',
      'Break 2 End',
      'Break 2 Duration (min)',
      'Break 3 Start',
      'Break 3 End',
      'Break 3 Duration (min)',
    ],
  ];
  
  fleet.vehicles.forEach((vehicle, vehicleIndex) => {
    const vehicleCount = vehicle.numberOfVehicles || 1;
    const vehicleNameBase = vehicle.name;
    
    vehicle.operatingHours.forEach((period, periodIndex) => {
      const duration = calculateDuration(period.start, period.end);
      const breaks = period.breaks || [];
      const breakCount = breaks.length;
      
      // Create shift ID: Vehicle-Task-StartTime
      const shiftId = `${vehicleNameBase}-T${String(periodIndex + 1).padStart(2, '0')}-${period.start}`;
      
      // Calculate break durations
      const breakDurations = breaks.map((b) => {
        if (b.start && b.end) {
          return calculateDuration(b.start, b.end);
        }
        return 0;
      });
      
      // For multiple vehicles, create separate rows
      for (let v = 0; v < vehicleCount; v++) {
        const vehicleName = vehicleCount > 1 ? `${vehicleNameBase}${String(v + 1).padStart(2, '0')}` : vehicleNameBase;
        
        const row: any[] = [
          vehicleName,
          shiftId,
          period.start,
          period.end,
          duration,
          breakCount,
        ];
        
        // Add break information (up to 3 breaks)
        for (let i = 0; i < 3; i++) {
          if (i < breaks.length) {
            const breakPeriod = breaks[i];
            row.push(
              breakPeriod.start || '',
              breakPeriod.end || '',
              breakDurations[i]
            );
          } else {
            row.push('', '', '');
          }
        }
        
        shiftData.push(row);
      }
    });
  });
  
  const shiftSheet = XLSX.utils.aoa_to_sheet(shiftData);
  shiftSheet['!cols'] = [
    { wch: 12 }, // Vehicle
    { wch: 25 }, // Shift ID
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 15 }, // Duration (min)
    { wch: 8 },  // Break
    { wch: 15 }, // Break 1 Start
    { wch: 15 }, // Break 1 End
    { wch: 18 }, // Break 1 Duration (min)
    { wch: 15 }, // Break 2 Start
    { wch: 15 }, // Break 2 End
    { wch: 18 }, // Break 2 Duration (min)
    { wch: 15 }, // Break 3 Start
    { wch: 15 }, // Break 3 End
    { wch: 18 }, // Break 3 Duration (min)
  ];
  XLSX.utils.book_append_sheet(workbook, shiftSheet, 'Shifts & Breaks');
  
  // Sheet 2: Hourly Fleet Capacity
  const hourlyData: any[][] = [
    ['Hour', 'Vehicles Available'],
  ];
  
  // Generate hourly capacity
  // For each hour, count unique vehicles that are operating (not on break) at that hour
  for (let hour = 3; hour <= 22; hour++) {
    const hourStr = minutesToTime(hour * 60);
    let availableCount = 0;
    
    fleet.vehicles.forEach((vehicle) => {
      const vehicleCount = vehicle.numberOfVehicles || 1;
      let vehicleAvailableInHour = false;
      
      // Check if vehicle has any shift operating at this hour (and not on break)
      vehicle.operatingHours.forEach((period) => {
        if (isTimeInPeriod(hourStr, period, true)) {
          vehicleAvailableInHour = true;
        }
      });
      
      if (vehicleAvailableInHour) {
        // Add the number of vehicles of this type
        availableCount += vehicleCount;
      }
    });
    
    hourlyData.push([hourStr, availableCount]);
  }
  
  const hourlySheet = XLSX.utils.aoa_to_sheet(hourlyData);
  hourlySheet['!cols'] = [
    { wch: 12 }, // Hour
    { wch: 20 }, // Vehicles Available
  ];
  XLSX.utils.book_append_sheet(workbook, hourlySheet, 'Hourly Capacity');
  
  // Sheet 3: 15-minute interval usage stats
  const timeSlots = generateTimeSlots(3, 23);
  const usageData: any[][] = [
    ['Vehicle', ...timeSlots],
  ];
  
  // Get all vehicles (expanded by numberOfVehicles)
  // Each vehicle shift is treated as a separate row
  const allVehicles: Array<{ name: string; vehicle: Vehicle; periodIndex: number; period: OperatingPeriod }> = [];
  fleet.vehicles.forEach((vehicle) => {
    const vehicleCount = vehicle.numberOfVehicles || 1;
    vehicle.operatingHours.forEach((period, periodIndex) => {
      for (let v = 0; v < vehicleCount; v++) {
        const vehicleName = vehicleCount > 1 
          ? `${vehicle.name}${String(v + 1).padStart(2, '0')}` 
          : vehicle.name;
        allVehicles.push({ name: `${vehicleName}-Shift${periodIndex + 1}`, vehicle, periodIndex, period });
      }
    });
  });
  
  // If no vehicles, add placeholder
  if (allVehicles.length === 0) {
    usageData.push(['No vehicles in fleet', ...timeSlots.map(() => '')]);
    const usageSheet = XLSX.utils.aoa_to_sheet(usageData);
    usageSheet['!cols'] = [{ wch: 20 }, ...timeSlots.map(() => ({ wch: 8 }))];
    XLSX.utils.book_append_sheet(workbook, usageSheet, 'Usage Stats');
    const fileName = `${fleet.name.replace(/[\/\\\?\*\[\]]/g, '_')}-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    return;
  }
  
  // Generate status for each vehicle at each time slot
  allVehicles.forEach(({ name, period, vehicle }) => {
    const row: any[] = [name];
    
    timeSlots.forEach((timeSlot) => {
      if (isTimeInPeriod(timeSlot, period, false)) {
        // Check if this is during a break
        const isOnBreak = period.breaks?.some((b: BreakPeriod) => {
          if (!b.start || !b.end) return false;
          return isTimeInPeriod(timeSlot, { start: b.start, end: b.end, breaks: undefined }, false);
        });
        
        row.push(isOnBreak ? 'B' : 'W');
      } else {
        row.push(''); // Vehicle not operating at this time
      }
    });
    
    usageData.push(row);
  });
  
  // Add summary row (count of vehicles with 'W' status at each time)
  const summaryRow: any[] = ['Summary'];
  timeSlots.forEach((timeSlot) => {
    let workingCount = 0;
    // Count how many vehicles have 'W' status at this time slot
    allVehicles.forEach(({ period }) => {
      if (isTimeInPeriod(timeSlot, period, true)) {
        workingCount++;
      }
    });
    summaryRow.push(workingCount);
  });
  usageData.push(summaryRow);
  
  const usageSheet = XLSX.utils.aoa_to_sheet(usageData);
  // Set column widths (first column wider, others narrower for time slots)
  const cols: any[] = [{ wch: 15 }]; // Vehicle column
  timeSlots.forEach(() => cols.push({ wch: 8 })); // Time slot columns
  usageSheet['!cols'] = cols;
  
  // Freeze first row and column
  usageSheet['!freeze'] = { xSplit: 1, ySplit: 1, topLeftCell: 'B2', activePane: 'bottomRight' };
  
  XLSX.utils.book_append_sheet(workbook, usageSheet, 'Usage Stats');
  
  // Generate filename
  const fileName = `${fleet.name.replace(/[\/\\\?\*\[\]]/g, '_')}-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

/**
 * Build rows for round-trip Excel/CSV export. One row per operating period (shift);
 * same Vehicle Name repeated for the same vehicle so import (merge-by-name) reproduces the fleet.
 * Columns match FleetMenu import expectations (VehicleRowData).
 */
function buildFleetExportRows(
  fleet: Fleet,
  allPoints: PointsWithCoords
): (string | number)[][] {
  const header = [
    'Vehicle Name',
    'Capacity (Standard)',
    'Capacity (Wheelchair)',
    'Number of Vehicles',
    'Shift #',
    'Shift Start',
    'Shift End',
    'Start Lat',
    'Start Lon',
    'End Lat',
    'End Lon',
    'Break #',
    'Break Start',
    'Break End',
    'Break Duration (s)',
  ];
  const rows: (string | number)[][] = [header];

  const getCoords = (pointId: string): { lat: number; lon: number } | undefined => {
    if (!pointId) return undefined;
    const point = allPoints.find((p) => p.id === pointId);
    if (!point?.coordinates || point.coordinates.length !== 2) return undefined;
    return { lat: point.coordinates[0], lon: point.coordinates[1] };
  };

  for (const vehicle of fleet.vehicles) {
    const startCoords = getCoords(vehicle.startLocation);
    const endCoords = getCoords(vehicle.endLocation);
    const numVehicles = vehicle.numberOfVehicles ?? 1;

    if (vehicle.operatingHours.length === 0) {
      rows.push([
        vehicle.name,
        vehicle.standardSeats,
        vehicle.wheelchairSeats,
        numVehicles,
        '',
        '',
        '',
        startCoords?.lat ?? '',
        startCoords?.lon ?? '',
        endCoords?.lat ?? '',
        endCoords?.lon ?? '',
        '',
        '',
        '',
        '',
      ]);
      continue;
    }

    vehicle.operatingHours.forEach((period, periodIndex) => {
      const breakCount = period.breaks?.length ?? 0;
      const firstBreak = period.breaks?.[0];
      const breakDurationSec =
        firstBreak?.start && firstBreak?.end
          ? calculateDuration(firstBreak.start, firstBreak.end) * 60
          : '';

      rows.push([
        vehicle.name,
        vehicle.standardSeats,
        vehicle.wheelchairSeats,
        numVehicles,
        periodIndex + 1,
        period.start,
        period.end,
        startCoords?.lat ?? '',
        startCoords?.lon ?? '',
        endCoords?.lat ?? '',
        endCoords?.lon ?? '',
        breakCount,
        firstBreak?.start ?? '',
        firstBreak?.end ?? '',
        breakDurationSec,
      ]);
    });
  }

  return rows;
}

/**
 * Export a single fleet to Excel for round-trip import. Columns match Fleet Menu import format;
 * re-import (with merge-by-vehicle-name) reproduces the same fleet structure.
 */
export function exportFleetToExcel(
  fleet: Fleet,
  allPoints: PointsWithCoords
): void {
  const rows = buildFleetExportRows(fleet, allPoints);
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [
    { wch: 18 },
    { wch: 10 },
    { wch: 12 },
    { wch: 10 },
    { wch: 8 },
    { wch: 12 },
    { wch: 12 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 8 },
    { wch: 12 },
    { wch: 12 },
    { wch: 18 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Fleet');
  const safeName = fleet.name.replace(/[\/\\\?\*\[\]]/g, '_');
  const date = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `${safeName}-fleet-${date}.xlsx`);
}

/**
 * Export a single fleet to CSV for round-trip import. Columns match Fleet Menu import format.
 */
export function exportFleetToCSV(
  fleet: Fleet,
  allPoints: PointsWithCoords
): void {
  const rows = buildFleetExportRows(fleet, allPoints);
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const safeName = fleet.name.replace(/[\/\\\?\*\[\]]/g, '_');
  const date = new Date().toISOString().split('T')[0];
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${safeName}-fleet-${date}.csv`);
  link.click();
  URL.revokeObjectURL(url);
}

